import VectorSource from 'ol/source/Vector.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import {proj as epsg21781} from '@geoblocks/proj/src/EPSG_21781.js';
import type {Options as VectorSourceOptions} from 'ol/source/Vector';
import type {FeatureLike} from 'ol/Feature';
import {Style, Icon} from 'ol/style.js';
import type {StyleFunction} from 'ol/style/Style';
import {POI_BASE_URL} from '../../envs.ts';
import {capitalize} from '../../utils.ts';
import type {Extent} from 'ol/extent';
import type {Projection} from 'ol/proj';
import {getLayerApiCode} from '../../layers/layerHelpers.ts';
import type {ClusteredLayerCode} from '../../layers/layerTypes.ts';
import type Feature from 'ol/Feature';

const format = new GeoJSON({
  dataProjection: epsg21781,
  featureProjection: 'EPSG:2056',
});
export class ClusteredSource extends VectorSource {
  constructor(
    code: ClusteredLayerCode,
    options?: VectorSourceOptions<Feature>,
  ) {
    super(
      Object.assign(
        {},
        {
          loader: async (
            extent: Extent,
            resolution: number,
            projection: Projection,
            success: (features: FeatureLike[]) => void,
            failure: () => void,
          ) => {
            let response: Response;
            try {
              response = await fetch(
                `${POI_BASE_URL}21781/clustered_${getLayerApiCode(
                  code,
                )}.geojson`,
              );
            } catch {
              this.removeLoadedExtent(extent);
              failure();
            }
            const data = await response.json();
            const features = format.readFeatures(data) as Feature[];
            features.forEach((feature) => {
              feature.set(
                'logoSrc',
                `/img/poi/${getLayerApiCode(
                  code,
                )}_${code === 'place' ? '22zoom' : '22'}.svg?ts11=TIMESTAMP11`,
              );
            });
            this.addFeatures(features);
            success(features);
          },
        },
        options,
      ),
    );
  }
}

export function createClusteredStyleFunction(
  code: ClusteredLayerCode,
): StyleFunction {
  return function clusteredStyle(
    feature: FeatureLike,
    resolution: number,
  ): Style {
    if (resolution < feature.get('resolution')) {
      // FIXME: port poi style from ui
      return style(feature.get('subtype') || code, resolution);
    } else {
      return null;
    }
  };
}

const styleCache: Record<string, Style> = {};

function style(code: ClusteredLayerCode, resolution: number): Style {
  const iconSize = 22;

  let scaledIconSize = 12;
  let prefixZoom = '';
  if (resolution >= 250) {
    scaledIconSize = 5;
  } else if (resolution <= 50) {
    if (code === 'place' && resolution <= 20) {
      scaledIconSize = 22;
      prefixZoom = 'zoom';
    } else {
      scaledIconSize = 22;
    }
  }

  const cacheKey = `${code}-${scaledIconSize}${prefixZoom}`;

  let style = styleCache[cacheKey];
  if (!style) {
    style = new Style({
      image: new Icon({
        scale: scaledIconSize / iconSize,
        src: `/img/poi/${capitalize(code)}_${iconSize}${prefixZoom}.svg?ts11=TIMESTAMP11`,
      }),
    });
    styleCache[cacheKey] = style;
  }
  return style;
}
